import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { logError } from '@/utils/errorUtils'
import type { OrpcClient } from '@/orpc/client'
import { useClient } from './useClient'

// TODO: Eventually GET /v2/patient/:id will return the full patient record with every
// attribute the web needs, replacing local Medplum reads. For now it carries id + status only.
export type PatientData = Awaited<ReturnType<OrpcClient['patientGet']>>

export function patientQueryKey(patientId: string | undefined) {
  return ['patient', patientId ?? 'none'] as const
}

// Backed by react-query so every usePatient(<sameId>) shares one cache entry: a single
// fetch serves all consumers, and calling refresh() on any of them re-fetches for all.
export function usePatient(patientId: string | undefined) {
  const client = useClient()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: patientQueryKey(patientId),
    enabled: !!patientId,
    queryFn: () => client.patientGet({ id: patientId! }),
  })

  useEffect(() => {
    if (query.error) {
      logError('patient', 'Failed to load patient', query.error)
    }
  }, [query.error])

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: patientQueryKey(patientId) }),
    [queryClient, patientId],
  )

  return { data: query.data ?? null, loading: query.isLoading, refresh }
}
